import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Lock, TrendingUp, Award, Timer } from 'lucide-react';

const fmt = (n, d = 4) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const ms = new Date(target).getTime() - now;
  if (ms <= 0) return <span className="text-[#0ECB81]">Unlocked</span>;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  return <span className="text-[#FCD535] font-mono">{days}d {hours}h</span>;
}

export default function BgnStakingSection() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [stakes, setStakes] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      // Public config is always available
      const pub = await api.get('/staking/bgn/config');
      setCfg(pub.data);
      if (user) {
        try {
          const mine = await api.get('/staking/bgn/my');
          setStakes(mine.data.stakes || []);
          setCfg(mine.data.config || pub.data);
        } catch (_e) { /* not logged in */ }
      } else {
        setStakes([]);
      }
    } catch (e) { /* offline */ }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line

  const monthlyPct = useMemo(() => ((cfg?.monthly_rate ?? 0.05) * 100), [cfg]);
  const lockMonths = cfg?.lock_months ?? 12;
  const totalYieldPct = monthlyPct * lockMonths;
  const rate = cfg?.usdt_per_bgn ?? 1.0;
  const projectedBgn = amount && !isNaN(parseFloat(amount)) ? parseFloat(amount) / rate : 0;
  const projectedInterest = projectedBgn * (cfg?.monthly_rate ?? 0.05) * lockMonths;
  const projectedTotal = projectedBgn + projectedInterest;

  const doStake = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < (cfg?.min_stake_usdt ?? 10)) {
      toast.error(`Minimum stake is ${cfg?.min_stake_usdt ?? 10} USDT`);
      return;
    }
    setBusy(true);
    try {
      await api.post('/staking/bgn/stake', { amount: amt });
      toast.success(`Locked ${amt} USDT — you'll earn ${fmt(amt * (cfg?.monthly_rate ?? 0.05) * lockMonths, 2)} BGN over ${lockMonths} months`);
      setOpen(false); setAmount('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stake failed');
    } finally { setBusy(false); }
  };

  const claim = async (stake_id) => {
    try {
      const r = await api.post(`/staking/bgn/claim/${stake_id}`);
      toast.success(`Claimed ${fmt(r.data.bgn_credited, 4)} BGN to your Spot wallet`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Claim failed');
    }
  };

  if (!cfg || cfg.enabled === false) {
    if (cfg && cfg.enabled === false) {
      return (
        <div className="border border-[#2B3139] bg-[#181A20] p-5 mb-6" data-testid="bgn-staking-disabled">
          <div className="text-[#848E9C] text-sm">BGN Staking plan is currently disabled by the admin.</div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mb-8" data-testid="bgn-staking-section">
      {/* Flagship hero card */}
      <div className="relative overflow-hidden border border-[#FCD535]/40 bg-gradient-to-br from-[#181A20] via-[#1E2026] to-[#0B0E11] p-6 sm:p-8">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FCD535] to-transparent" />
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#FCD535]/10 blur-3xl pointer-events-none" />

        <div className="flex items-start justify-between flex-wrap gap-4 relative">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase font-mono tracking-widest text-[#FCD535] mb-2">
              <Sparkles className="w-3 h-3" />
              Official BITGONODE Token Plan
            </div>
            <h2 className="font-display text-white text-3xl sm:text-4xl font-bold tracking-tighter">Stake USDT · Earn BGN</h2>
            <p className="text-[#848E9C] text-sm mt-2 max-w-xl">
              Lock USDT into the exchange's flagship BGN token plan. Receive BGN 1:{fmt(1 / (rate || 1), 4)} on the day you stake and accrue monthly interest until maturity.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <Button
              onClick={() => setOpen(true)}
              className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm h-11 px-6"
              data-testid="bgn-stake-open-btn"
            >
              Stake Now
            </Button>
            <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight">Stake USDT for BGN</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-[#0B0E11] border border-[#2B3139] p-2.5">
                    <div className="text-[#848E9C] uppercase tracking-wider text-[10px]">Rate</div>
                    <div className="font-mono text-[#FCD535] mt-0.5">{monthlyPct.toFixed(2)}%/mo</div>
                  </div>
                  <div className="bg-[#0B0E11] border border-[#2B3139] p-2.5">
                    <div className="text-[#848E9C] uppercase tracking-wider text-[10px]">Lock</div>
                    <div className="font-mono text-white mt-0.5">{lockMonths} mo</div>
                  </div>
                  <div className="bg-[#0B0E11] border border-[#2B3139] p-2.5">
                    <div className="text-[#848E9C] uppercase tracking-wider text-[10px]">Total yield</div>
                    <div className="font-mono text-[#0ECB81] mt-0.5">{totalYieldPct.toFixed(1)}%</div>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#848E9C] uppercase tracking-wider">Amount (USDT)</label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder={`min ${cfg?.min_stake_usdt ?? 10} USDT`}
                    className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                    data-testid="bgn-stake-amount-input"
                  />
                </div>
                {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
                  <div className="bg-[#0B0E11]/60 border border-[#2B3139] p-3 space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-[#848E9C]">You lock</span><span className="font-mono text-white">{fmt(parseFloat(amount), 4)} USDT</span></div>
                    <div className="flex justify-between"><span className="text-[#848E9C]">You receive (BGN principal)</span><span className="font-mono text-white">{fmt(projectedBgn, 4)} BGN</span></div>
                    <div className="flex justify-between"><span className="text-[#848E9C]">Interest over {lockMonths} months</span><span className="font-mono text-[#0ECB81]">+{fmt(projectedInterest, 4)} BGN</span></div>
                    <div className="flex justify-between pt-1 border-t border-[#2B3139]"><span className="text-[#FCD535]">Total at maturity</span><span className="font-mono text-[#FCD535] font-semibold">{fmt(projectedTotal, 4)} BGN</span></div>
                  </div>
                )}
                <div className="text-[10px] text-[#848E9C] leading-relaxed">
                  Your USDT is deducted from your Spot balance and locked for {lockMonths} months.
                  BGN principal + interest is credited to your Spot BGN balance upon claim after unlock.
                </div>
              </div>
              <DialogFooter>
                <Button onClick={doStake} disabled={busy || !user}
                  className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
                  data-testid="bgn-stake-confirm-btn">
                  {!user ? 'Log in to stake' : busy ? 'Locking…' : 'Confirm & Lock'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 relative">
          <div className="bg-[#0B0E11]/60 backdrop-blur border border-[#2B3139] p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#848E9C]"><TrendingUp className="w-3 h-3" />Monthly rate</div>
            <div className="font-display text-white text-2xl font-bold mt-1">{monthlyPct.toFixed(2)}<span className="text-sm text-[#848E9C] ml-1">%</span></div>
          </div>
          <div className="bg-[#0B0E11]/60 backdrop-blur border border-[#2B3139] p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#848E9C]"><Lock className="w-3 h-3" />Lock period</div>
            <div className="font-display text-white text-2xl font-bold mt-1">{lockMonths}<span className="text-sm text-[#848E9C] ml-1">months</span></div>
          </div>
          <div className="bg-[#0B0E11]/60 backdrop-blur border border-[#2B3139] p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#848E9C]"><Award className="w-3 h-3" />Total yield</div>
            <div className="font-display text-[#0ECB81] text-2xl font-bold mt-1">{totalYieldPct.toFixed(1)}<span className="text-sm text-[#848E9C] ml-1">%</span></div>
          </div>
          <div className="bg-[#0B0E11]/60 backdrop-blur border border-[#2B3139] p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#848E9C]"><Sparkles className="w-3 h-3" />USDT → BGN</div>
            <div className="font-display text-[#FCD535] text-2xl font-bold mt-1">1:{fmt(1 / (rate || 1), 4)}</div>
          </div>
        </div>
      </div>

      {/* My stakes table */}
      {user && stakes.length > 0 && (
        <div className="border border-[#2B3139] bg-[#181A20] mt-4" data-testid="bgn-my-stakes">
          <div className="px-4 py-3 border-b border-[#2B3139] flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-white flex items-center gap-2">
              <Timer className="w-3.5 h-3.5 text-[#FCD535]" /> My BGN Stakes
            </div>
            <div className="text-[10px] text-[#848E9C]">{stakes.filter(s => s.status !== 'claimed').length} active</div>
          </div>
          <div className="hidden sm:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]/60">
            <div className="col-span-2">Locked</div>
            <div className="col-span-2">Principal</div>
            <div className="col-span-2">Accrued</div>
            <div className="col-span-2">At maturity</div>
            <div className="col-span-2">Unlocks in</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {stakes.map(s => (
            <div key={s.stake_id} className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-sm" data-testid={`bgn-stake-row-${s.stake_id}`}>
              <div className="sm:col-span-2 font-mono text-white">{fmt(s.usdt_staked, 2)} <span className="text-[#848E9C] text-xs">USDT</span></div>
              <div className="sm:col-span-2 font-mono text-white">{fmt(s.bgn_principal, 4)} <span className="text-[#848E9C] text-xs">BGN</span></div>
              <div className="sm:col-span-2 font-mono text-[#0ECB81]">+{fmt(s.accrued_bgn, 4)}</div>
              <div className="sm:col-span-2 font-mono text-[#FCD535]">{fmt(s.projected_total_payout_bgn, 4)}</div>
              <div className="sm:col-span-2 text-xs">
                {s.status === 'claimed' ? (
                  <span className="text-[#848E9C]">Claimed</span>
                ) : s.is_unlocked ? (
                  <span className="text-[#0ECB81]">Unlocked ✓</span>
                ) : (
                  <Countdown target={s.unlock_at} />
                )}
              </div>
              <div className="sm:col-span-2 text-right">
                {s.status !== 'claimed' && s.is_unlocked ? (
                  <Button
                    onClick={() => claim(s.stake_id)}
                    size="sm"
                    className="bg-[#0ECB81] hover:bg-[#0BB273] text-[#0B0E11] rounded-sm h-7 text-xs font-semibold px-3"
                    data-testid={`bgn-claim-${s.stake_id}`}
                  >
                    Claim
                  </Button>
                ) : s.status === 'claimed' ? (
                  <span className="text-[10px] text-[#848E9C] font-mono">+{fmt(s.bgn_paid_out ?? 0, 4)} BGN</span>
                ) : (
                  <span className="text-[10px] text-[#848E9C]">Locked</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
